import MDTypography from 'src/components/md/MDTypography';
import { Spinner } from 'src/components/ui/spinner';
import { WarningCircleIcon } from '@phosphor-icons/react';

import { T, useGT } from 'gt-react';
import useProductVariant from 'src/api/inventory-management/products/useProductVariant';

// New product variant list item component
const ProductVariantListItem: React.FC<{
  variantId: string;
  isSelected: boolean;
  onSelect: (variantId: string, productId: string) => void;
}> = ({ variantId, isSelected, onSelect }) => {
  const gt = useGT();
  const { data, isLoading, error } = useProductVariant(variantId);

  const productVariant = data?.productVariant;

  return (
    <li key={variantId} className="border-b last:border-b-0 p-0">
      <button
        type="button"
        role="radio"
        aria-checked={isSelected}
        className="flex w-full items-center px-4 py-2 text-left hover:bg-muted/50"
        onClick={() => {
          if (productVariant) {
            onSelect(productVariant.id, productVariant.productId);
          }
        }}
      >
        {/* Visual radio indicator. A Radix RadioGroupItem cannot be used here
            because these items are not wrapped in a RadioGroup context; the
            enclosing button carries the radio role/selection state instead. */}
        <span
          aria-hidden="true"
          className={`mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
            isSelected ? 'border-primary' : 'border-input'
          }`}
        >
          {isSelected && <span className="h-2 w-2 rounded-full bg-primary" />}
        </span>
        {isLoading ? (
          <div className="flex items-center py-2 ml-4">
            <Spinner />
            <MDTypography variant="body2" ml={2}>
              <T context="loading state while fetching product variant">
                Loading variant information...
              </T>
            </MDTypography>
          </div>
        ) : error ? (
          <div className="flex items-center py-2 ml-4">
            <WarningCircleIcon size={20} className="mr-1" />
            <MDTypography variant="body2" color="error.main">
              {error.message ||
                gt('Error loading variant', {
                  context: 'error when product variant failed to load',
                })}
            </MDTypography>
          </div>
        ) : productVariant ? (
          <div className="flex items-center w-full">
            <span className="text-sm">
              {(productVariant.originMetadata?.NAMEINVC as string) ||
                gt('(No name)', {
                  context:
                    'placeholder when product variant has no display name',
                })}
            </span>
          </div>
        ) : (
          <span className="text-sm">
            {gt('Variant not found', {
              context: 'product variant could not be loaded',
            })}
          </span>
        )}
      </button>
    </li>
  );
};

export default ProductVariantListItem;
